/**
 * News · Detalle de artículo — cierre del loop de QA.
 *
 * Muestra TODOS los tags de la clasificación del LLM (categoría/subcategoría
 * IPTC, CEFR, sensibilidad, tags libres, our_summary, discussion_prompts),
 * link al artículo original, y dos acciones:
 *   - Ocultar (POST /hide): status → hidden, refetch inmediato.
 *   - Re-clasificar (POST /reclassify): encola un job async; el worker
 *     reprocesa y cambia `classified_at`. Damos feedback de "encolado" e
 *     invalidamos para refetchear cuando el operador vuelva.
 */
import { useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchNewsArticle,
  hideNewsArticle,
  reclassifyNewsArticle,
} from '../../api/admin/news.js';
import { AdminApiError } from '../../api/admin/_core.js';
import { formatDateTime } from '../../lib/format.js';
import { CefrBadge, SensitivityBadge, StatusBadge } from './badges.js';

export function ArticleDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'news', 'article', id],
    queryFn: () => fetchNewsArticle(id as string),
    enabled: Boolean(id),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['admin', 'news', 'article', id] });
    void qc.invalidateQueries({ queryKey: ['admin', 'news', 'articles'] });
  };

  const hideMut = useMutation({
    mutationFn: () => hideNewsArticle(id as string),
    onSuccess: () => {
      setToast({ kind: 'ok', text: 'Artículo ocultado' });
      invalidate();
    },
    onError: (err: unknown) => {
      setToast({ kind: 'err', text: err instanceof AdminApiError ? err.message : String(err) });
    },
  });

  const reclassifyMut = useMutation({
    mutationFn: () => reclassifyNewsArticle(id as string),
    onSuccess: (res) => {
      setToast({
        kind: 'ok',
        text: `Re-clasificación encolada (job ${res.jobId.slice(0, 8)}…). El cambio se verá tras el reprocesamiento del worker.`,
      });
      invalidate();
    },
    onError: (err: unknown) => {
      setToast({ kind: 'err', text: err instanceof AdminApiError ? err.message : String(err) });
    },
  });

  if (query.isPending) return <div className="loading">Cargando…</div>;
  if (query.isError) return <div className="loading">Error: {query.error.message}</div>;

  const a = query.data.article;
  const busy = hideMut.isPending || reclassifyMut.isPending;

  return (
    <>
      <div className="page-header">
        <div>
          <p className="page-sub">
            <Link to="/news" className="tt-tert">← Artículos</Link>
            {' · '}
            <span className="tt-tert">{a.sourceName}</span>
          </p>
          <h1 className="page-title">{a.title}</h1>
        </div>
        <div className="flex ai-c gap-3">
          {toast ? (
            <span
              className={toast.kind === 'ok' ? 'config-toast ok' : 'config-toast err'}
              role="status"
            >
              {toast.text}
            </span>
          ) : null}
          <button
            type="button"
            className="btn-warn"
            disabled={busy || a.status === 'hidden'}
            onClick={() => reclassifyMut.mutate()}
            title="Encola un job async de re-clasificación"
          >
            {reclassifyMut.isPending ? 'Encolando…' : 'Re-clasificar'}
          </button>
          <button
            type="button"
            className="btn-danger"
            disabled={busy || a.status === 'hidden'}
            onClick={() => hideMut.mutate()}
          >
            {hideMut.isPending ? 'Ocultando…' : a.status === 'hidden' ? 'Oculto' : 'Ocultar'}
          </button>
        </div>
      </div>

      <div className="news-detail-grid">
        {/* Clasificación del LLM — el foco del QA */}
        <section className="card">
          <header className="card-head">
            <h3>Clasificación LLM</h3>
            <span className="tt-tert mono" style={{ fontSize: 'var(--fs-xs)' }}>
              {a.classifiedAt ? `clasificado ${formatDateTime(a.classifiedAt)}` : 'sin clasificar'}
            </span>
          </header>
          <dl className="kv">
            <dt>Categoría IPTC</dt>
            <dd>{a.iptcCategory ?? <span className="muted">—</span>}</dd>
            <dt>Subcategoría</dt>
            <dd>{a.iptcSubcategory ?? <span className="muted">—</span>}</dd>
            <dt>Nivel CEFR</dt>
            <dd><CefrBadge value={a.cefr} /></dd>
            <dt>Sensibilidad</dt>
            <dd><SensitivityBadge value={a.sensitivity} /></dd>
            <dt>Estado</dt>
            <dd><StatusBadge value={a.status} /></dd>
            <dt>Tags</dt>
            <dd>
              {a.tags.length > 0 ? (
                <span className="flex ai-c gap-2" style={{ flexWrap: 'wrap' }}>
                  {a.tags.map((t) => (
                    <span key={t} className="badge b-neutral">{t}</span>
                  ))}
                </span>
              ) : (
                <span className="muted">—</span>
              )}
            </dd>
          </dl>

          <div className="news-block">
            <h4 className="news-block-title">Resumen propio (our_summary)</h4>
            <p className="news-summary">
              {a.ourSummary ?? <span className="muted">— sin resumen</span>}
            </p>
          </div>

          <div className="news-block">
            <h4 className="news-block-title">Preguntas de apertura (discussion_prompts)</h4>
            {a.discussionPrompts.length > 0 ? (
              <ol className="news-prompts">
                {a.discussionPrompts.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ol>
            ) : (
              <p className="muted">— sin preguntas</p>
            )}
          </div>
        </section>

        {/* Metadatos crudos del feed */}
        <section className="card">
          <header className="card-head">
            <h3>Origen</h3>
          </header>
          <dl className="kv">
            <dt>Fuente</dt>
            <dd>{a.sourceName} <span className="tt-tert mono">({a.sourceSlug})</span></dd>
            <dt>Autor</dt>
            <dd>{a.author ?? <span className="muted">—</span>}</dd>
            <dt>Idioma</dt>
            <dd className="mono">{a.lang}</dd>
            <dt>Publicado</dt>
            <dd>{formatDateTime(a.publishedAt)}</dd>
            <dt>Ingerido</dt>
            <dd>{formatDateTime(a.fetchedAt)}</dd>
            <dt>Original</dt>
            <dd>
              <a href={a.url} target="_blank" rel="noreferrer noopener">Abrir artículo ↗</a>
            </dd>
          </dl>

          {a.excerpt ? (
            <div className="news-block">
              <h4 className="news-block-title">Excerpt</h4>
              <p className="news-summary tt-sec">{a.excerpt}</p>
            </div>
          ) : null}

          {a.imageUrl ? (
            <div className="news-block">
              <img
                src={a.imageUrl}
                alt=""
                className="news-image"
                loading="lazy"
              />
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
}
